import React, { useEffect } from 'react'
import { useFormik, Form, FormikProvider } from 'formik'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'

import { useAddServiceMutation } from 'shared/store/useAddtionalServices'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import InputField from 'shared/components/InputField'
import { useProfileQuery } from 'shared/store/useProfile'

import { DialogActions } from '@mui/material'
import { CreateServiceFormikType } from '../../../types'
import { createServiceSchema } from '../../../schema'

interface Props {
   handleClose: () => void
   locationUuid: string
}

export default function CreateServiceModal({ handleClose, locationUuid }: Props) {
   const { mutate, isLoading } = useAddServiceMutation()
   const { data } = useProfileQuery()
   const { t } = useTranslation(['TRANSLATION', 'SERVICES'])

   const formik = useFormik<CreateServiceFormikType>({
      initialValues: {
         name: '',
         defaultGrossPrice: 0,
         defaultNetPrice: 0,
         taxRate: 23,
      },
      validationSchema: createServiceSchema,
      onSubmit: createService,
   })

   const { values, isValid, setFieldValue } = formik

   function createService() {
      mutate(
         { payload: { ...values }, locationUuid },
         {
            onSuccess: () => {
               handleClose()
            },
         }
      )
   }

   useEffect(() => {
      if (values.taxRate !== 0) {
         const newValue = Number((values.defaultNetPrice * (1 + values.taxRate / 100)).toFixed(2))
         setFieldValue('defaultGrossPrice', newValue)
      }
   }, [values.taxRate])

   function calculateNewNetPrice() {
      if (values.taxRate !== 0 && values.defaultGrossPrice !== 0) {
         const newNetPrice = Number((values.defaultGrossPrice / (1 + values.taxRate / 100)).toFixed(2))
         setFieldValue('defaultNetPrice', newNetPrice)
      }
   }

   function calculateNewGrossPrice() {
      if (values.taxRate !== 0 && values.defaultNetPrice !== 0) {
         const newGrossPrice = Number((values.defaultNetPrice * (1 + values.taxRate / 100)).toFixed(2))
         setFieldValue('defaultGrossPrice', newGrossPrice)
      }
   }

   return (
      <>
         <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
               {t('SERVICES:createServiceLabel')}
            </Typography>
            <FormikProvider value={formik}>
               <Form>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           gap: 2,
                           justifyContent: 'center',
                           minWidth: 300,
                        }}
                     >
                        <InputField<CreateServiceFormikType>
                           variant="standard"
                           label={t('TRANSLATION:formFields.serviceName')}
                           name="name"
                           onFocus={(e) => e.target.select()}
                           required
                           inputProps={{
                              maxLength: 32,
                           }}
                        />
                        <InputField<CreateServiceFormikType>
                           variant="standard"
                           type="Number"
                           onFocus={(e) => e.target.select()}
                           inputProps={{
                              min: 0,
                              step: 0.5,
                           }}
                           label="VAT"
                           name="taxRate"
                        />
                        <InputField<CreateServiceFormikType>
                           variant="standard"
                           type="Number"
                           onBlur={calculateNewGrossPrice}
                           onFocus={(e) => e.target.select()}
                           inputProps={{
                              min: 0,
                              step: 0.01,
                           }}
                           label={`${t('TRANSLATION:formFields.defaultNetPrice')} (${data?.formattedCurrency})`}
                           name="defaultNetPrice"
                           required
                        />
                        <InputField<CreateServiceFormikType>
                           variant="standard"
                           type="Number"
                           onBlur={calculateNewNetPrice}
                           onFocus={(e) => e.target.select()}
                           inputProps={{
                              min: 0,
                              step: 0.01,
                           }}
                           label={`${t('TRANSLATION:formFields.defaultGrossPrice')} (${data?.formattedCurrency})`}
                           name="defaultGrossPrice"
                           required
                        />
                     </Box>
                  </Box>
               </Form>
            </FormikProvider>
         </DialogContent>
         <DialogActions>
            <OutlinedButton variant="outlined" onClick={handleClose}>
               {t('TRANSLATION:cancel')}
            </OutlinedButton>
            <ContainedButton disabled={isLoading || !isValid} onClick={createService} variant="contained" type="submit">
               {t('TRANSLATION:save')}
            </ContainedButton>
         </DialogActions>
      </>
   )
}
